<template>

  <v-container fluid class="page page-receiver-credit-list">

    <!---------------->
    <!-- Breadcrumb -->
    <!---------------->
    <v-row class="mt-3 mb-6">
      <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
        <v-icon small class="mr-2">mdi-credit-card</v-icon>
        <div>Credits</div>
      </v-col>
    </v-row>

    <!----------------------------->
    <!-- Page Label and Controls -->
    <!----------------------------->
    <v-container fluid class="page-content">
      <v-row no-gutters>
        <v-col cols="12" xs="12" sm="12" md="4" lg="4">
          <span class="mr-3 page-label">Credits</span>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="info-timezone" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
            </template>
            <div>Dates & times are: {{ activeTimezone }}</div>
          </v-tooltip>
        </v-col>
        <!-- Filter options -->
        <v-col cols="12" xs="12" sm="12" md="8" lg="8" align="right" class="filter-options-container">

        </v-col>
      </v-row>
    </v-container>

    <!----------------->
    <!-- Credit List -->
    <!----------------->
    <v-container fluid class="mt-4 page-content">
      <v-row no-gutters align="center" justify="start">
        <!-- Find by order number -->
        <v-col cols="12" xs="12" sm="6" md="4" lg="3">
          <v-text-field
              class="pl-2 pt-0 order-number-finder"
              clearable
              single-line
              hide-details
              label="Order number"
              append-icon="mdi-magnify"
              v-model="search" />
        </v-col>
        <!-- Bulk action -->
        <v-col cols="12" xs="12" sm="6" md="8" lg="9" align="right">
          <template v-if="selected.length > 0">
            <span class="mr-4" style="color:#9c9c9c;" v-if="selectedItemCount > 0">{{ selectedItemCount }} Selected</span>
            <v-btn color="blue" width="180" @click="handleDownloadMultipleInvoices">
              <span v-if="selectedItemCount === 1">Download Invoice</span>
              <span v-else>Download Invoices</span>
            </v-btn>
          </template>
        </v-col>
      </v-row>

      <v-data-table
          class="mt-8"
          :headers="headers"
          :items="orderItems"
          :search="search"
          :options.sync="dataOptions"
          :footer-props="paginationOptions"
          :loading="isDataLoading"
          :server-items-length="totalItems"
          :show-select="true"
          v-model="selected">

        <!-- routeleaf credit order number (link to order info page) -->
        <template v-slot:[`item.order_number`]="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="yellow darken-4"
                  style="cursor:pointer;font-size:21px;margin-right:3px;padding-bottom:5px;"
                  v-bind="attrs"
                  v-on="on">
                mdi-credit-card
              </v-icon>
            </template>
            <div style="font-weight:600">Order Credit</div>
          </v-tooltip>
          <span style="font-size:1.2em">
            <router-link :to="generateOrderInfoPageLink(item)">
              {{ item.thirdparty_order_number }}
            </router-link>
          </span>
        </template>

        <!-- order total -->
        <template v-slot:item.order_details.total="{ item }">
          <span class="order-total">{{ formatTotal(item.order_details) }}</span>
        </template>

        <!-- provider alias -->
        <template v-slot:item.provider_alias="{ item }">
          <span class="provider-name">{{ formatProvider(item.provider_alias) }}</span>
        </template>

        <!-- provider brand -->
        <template v-slot:[`item.thirdparty_brand_name`]="{ item }">
          <span>{{ item.thirdparty_brand_name }}</span>
        </template>

        <!-- pickup location (shipper) -->
        <template v-slot:[`item.shipper.name`]="{ item }">
          {{ formatLocation(item.shipper) }}
        </template>

        <!-- dropoff location (receiver) -->
        <template v-slot:[`item.receiver.name`]="{ item }">
          <!-- (note on approval requirements) -->
          <v-tooltip top v-if="item.receiver.receiver_status === 'PENDING_APPROVAL'">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="yellow darken-3"
                  style="cursor:pointer;font-size:21px;margin-right:3px;"
                  v-bind="attrs"
                  v-on="on">
                mdi-alert
              </v-icon>
            </template>
            <div>Order cannot be scheduled until Receiver is approved.</div>
          </v-tooltip>
          <router-link :to="generateLocationPageLink(item.receiver)">
            {{ formatLocation(item.receiver) }}
          </router-link>
        </template>

        <!-- issued date -->
        <template v-slot:[`item.delivered_at`]="{ item }">
          <span>{{ formatDate(item.delivered_at) }}</span>
        </template>

        <!-- attachment docs -->
        <template v-slot:item.attachment_docs="{ item }">
          <span>{{ Object.keys(item.attachment_docs).length }}</span>
        </template>

        <!-- (download menu) -->
        <template v-slot:[`item.id`]="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="white"
                  v-bind="attrs"
                  v-on="on">
                mdi-dots-horizontal
              </v-icon>
            </template>
            <v-list>
              <v-list-item class="px-2 menu-item">
                <v-btn plain @click="downloadOrderInvoice(item)">Credit Invoice</v-btn>
              </v-list-item>
              <v-list-item class="px-2 menu-item" v-if="Object.keys(item.attachment_docs).length > 0">
                <v-btn plain @click="downloadOrderAttachments(item)">Attachment Docs</v-btn>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-container>

  </v-container>

</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'ReceiverCreditListPage',

  data () {
    return {
      headers: [
        { text: 'Order #', value: 'order_number' },
        // { text: 'Provider', value: 'provider_alias' },
        { text: 'Brand', width: '120px', value: 'thirdparty_brand_name' },
        { text: 'Total', value: 'order_details.total' },
        { text: 'From', value: 'shipper.name' },
        { text: 'To', value: 'receiver.name' },
        { text: 'Issued', align: 'center', value: 'delivered_at' },
        { text: 'Attachment Docs', align: 'center', value: 'attachment_docs' },
        { text: 'Download', sortable: false, align: 'center', value: 'id', width: '5%' }
      ],
      isDataLoading: false,
      dataLoadingError: null,
      orderData: null,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50, 100]
      },
      dataOptions: {
        itemsPerPage: 10,
        // NOTE - Currently enabled for single field sort only !!!
        sortBy: ['delivered_at'],
        sortDesc: [true]
      },
      search: null,
      selected: []
    }
  },

  computed: {
    activeTimezone () {
      return this.$store.getters['app/defaultTimezone']
    },
    orderItems () {
      return (this.orderData) ? this.orderData.items : []
    },
    dataMeta () {
      return (this.orderData) ? this.orderData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    },
    selectedItemCount () {
      return (this.selected) ? this.selected.length : 0
    },
    ...mapState('app', {
      downloadStatus: state => state.downloadStatus
    })
  },

  watch: {
    // Observe changes invoked by the data-table component (e.g. pagination, sorting)
    dataOptions: {
      handler () {
        this.queryData()
      },
      deep: true
    },
    // Observe changes to the status filter
    statusFilter: function (updated) {
      this.queryData()
    },
    // Observe changes to the search field
    search: function (updated) {
      this.queryData()
    }
  },

  methods: {
    // Display a date
    formatDate (date) {
      return (date) ? this.$root.utils.format(date, 'MMMM D, YYYY') : '-'
    },

    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.format(date, 'MMM D, YYYY h:mma') : ''
    },

    // Display total
    formatTotal (orderDetails = {}) {
      const totalAsNum = (orderDetails.total) ? Number(orderDetails.total) : 0
      return `$${totalAsNum.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    },

    // Display provider name
    formatProvider (alias) {
      return (alias) ? this.$root.providerAliasToName(alias) : ''
    },

    // Display location name
    formatLocation (loc) {
      return (loc && loc.name) ? loc.name : '-'
    },

    // Generate link data for shipper/receiver page (for a provided location item)
    generateLocationPageLink (loc) {
      if (loc && loc.model) {
        if (loc.model === 'Shipper') {
          return {
            name: 'shipper-item',
            params: { shipperId: loc.id }
          }
        } else if (loc.model === 'Receiver') {
          return {
            name: 'receiver-item',
            params: { receiverId: loc.id }
          }
        } else {
          return ''
        }
      } else {
        return ''
      }
    },

    // Generate link data for order info page
    generateOrderInfoPageLink (item) {
      if (item && item.model) {
        return {
          name: 'receiver-order-info-summary',
          params: { orderId: item.id }
        }
      } else {
        return ''
      }
    },

    // Handle bulk order invoice download action
    handleDownloadMultipleInvoices () {
      this.$root.sendDownloadOrderInvoiceRequest(this.selected)
      this.selected = []
    },

    // Send request to download order invoice
    downloadOrderInvoice (order) {
      this.$root.sendDownloadOrderInvoiceRequest([order])
    },

    // Send request to download order attachments
    downloadOrderAttachments (order) {
      this.$root.sendDownloadOrderAttachmentsRequest(order)
    },

    // Perform query of placed orders
    async queryData () {
      this.isDataLoading = true

      try {
        // Build sort
        // NOTE - Currently enabled for single field sort only !!!
        let orderBy = null
        const sortBy = this.dataOptions.sortBy
        const sortDesc = this.dataOptions.sortDesc
        if (sortBy && sortBy.length > 0) {
          const isDesc = (sortDesc && sortDesc.length > 0) ? sortDesc[0] : false
          const prefix = (isDesc) ? '-' : ''
          orderBy = `${prefix}${sortBy[0]}`
        }

        // Build pagination
        const page = this.dataOptions.page || 1
        const limit = this.dataOptions.itemsPerPage
        const skip = (page === 1) ? 0 : (page - 1) * limit

        // Build request input
        const params = {
          skip,
          limit,
          with: 'shipper,receiver,order_details'
        }

        // Apply sorting
        if (orderBy) params.order_by = orderBy

        // Apply search query (for order_number only)
        if (this.search) params['order_number.contains'] = this.search

        // Retrieve results
        this.orderData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'receiver-credits',
          opts: { params }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style>
  .page-receiver-credit-list .info-timezone {
    font-size: 1.25em;
    color: #6a6a6a;
    padding-bottom: 12px;
    cursor:pointer;
  }

  .page-receiver-credit-list .filter-options-container {
    /* border: 1px solid #525252;
    border-radius: 4px; */
  }

  .page-receiver-credit-list .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .page-receiver-credit-list .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .page-receiver-credit-list .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 120px;
  }

  .page-receiver-credit-list .v-data-table a {
    text-decoration: none;
  }
  .page-receiver-credit-list .v-data-table a:hover {
    text-decoration: underline;
  }

  @media screen and (max-width: 940px) {
    .page-receiver-credit-list .order-number-finder {
      margin-top: 14px;
    }
  }
</style>
